import { useEffect, useRef, useState } from 'react';

import { docIO, SAVE_DELAY_MS } from '../../lib/doc-io.js';
import { TEXTURE_DEFAULTS, TEXTURE_PATTERNS } from '../../lib/model-doc.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { useProjectFiles } from '../../state/project-files.js';
import { Card, ColorField, EmptyState, NumberField, PropRow, SelectField, TextField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { ObjectFields } from './PartInspector.js';

/**
 * Procedural texture presets: project data files
 * `textures/presets/<name>.texpreset.json` holding a model material's
 * `texture` settings (plus optional color / roughness / metallic). Edited in
 * the model tab's Textures panel and applied to materials from the
 * Materials panel (the settings are copied into the material).
 */
export interface TexturePreset {
  format?: string;
  name?: string;
  texture: Record<string, unknown>;
  color?: number[];
  roughness?: number;
  metallic?: number;
}

export const PRESET_DIR = 'textures/presets';
export const PRESET_SUFFIX = '.texpreset.json';
const PRESET_FORMAT = 'prism.texpreset/1';

/** Starter presets written by "Add starter presets" (all editable afterwards). */
const STARTERS: Record<string, TexturePreset> = {
  worn_planks: { name: 'Worn planks', texture: { pattern: 'planks', size: 1.2, wear: 0.4, bump: 1 }, color: [0.55, 0.38, 0.22], roughness: 0.8 },
  brushed_steel: { name: 'Brushed steel', texture: { pattern: 'brushed', size: 0.3, scratches: 200 }, color: [0.78, 0.78, 0.8], roughness: 0.35, metallic: 1 },
  mossy_stone: { name: 'Mossy stone', texture: { pattern: 'stone', size: 0.8, wear: 0.5, color2: [0.25, 0.35, 0.15] }, color: [0.5, 0.48, 0.45], roughness: 0.9 },
  red_bricks: { name: 'Red bricks', texture: { pattern: 'bricks', size: 0.6 }, color: [0.6, 0.25, 0.18], roughness: 0.85 },
  soft_fabric: { name: 'Soft fabric', texture: { pattern: 'fabric', size: 0.15, bump: 0.5 }, color: [0.3, 0.35, 0.55], roughness: 0.95 },
  flat_toon: { name: 'Flat (stylized)', texture: { pattern: 'noise', size: 2, contrast: 0.2, bump: 0.1, roughnessVariation: 0, resolution: 128 }, roughness: 0.7 },
};

export function isPresetPath(path: string): boolean {
  return path.toLowerCase().endsWith(PRESET_SUFFIX);
}

/** Paths of every texture preset in the project. */
export function usePresetPaths(): string[] {
  const entries = useProjectFiles((s) => s.entries);
  return entries
    .filter((e) => e.kind === 'file' && isPresetPath(e.path))
    .map((e) => e.path)
    .sort((a, b) => a.localeCompare(b));
}

export function parsePreset(text: string): TexturePreset | null {
  try {
    const v = JSON.parse(text) as Partial<TexturePreset>;
    if (!v || typeof v !== 'object' || typeof v.texture !== 'object' || v.texture === null || Array.isArray(v.texture)) return null;
    return v as TexturePreset;
  } catch {
    return null;
  }
}

export function presetLabel(path: string, preset?: TexturePreset | null): string {
  return preset?.name || path.slice(path.lastIndexOf('/') + 1, -PRESET_SUFFIX.length);
}

/** Loaded presets by path (re-read when the file list changes). */
export function usePresets(): Record<string, TexturePreset | null> {
  const paths = usePresetPaths();
  const projectId = useProjectFiles((s) => s.projectId);
  const [loaded, setLoaded] = useState<Record<string, TexturePreset | null>>({});
  const key = paths.join('|');
  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    void Promise.all(
      paths.map(async (p) => [p, await docIO().read(projectId, p).then(parsePreset, () => null)] as const),
    ).then((pairs) => {
      if (!cancelled) setLoaded(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [key, projectId]);
  return loaded;
}

async function writePreset(path: string, preset: TexturePreset): Promise<void> {
  const projectId = useProjectFiles.getState().projectId;
  if (projectId == null) return;
  await docIO().write(projectId, path, `${JSON.stringify({ format: PRESET_FORMAT, ...preset }, null, 2)}\n`);
}

function slug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'preset';
}

async function newPreset(existing: readonly string[]): Promise<void> {
  const name = await promptText('New texture preset', 'My texture');
  if (!name) return;
  let path = `${PRESET_DIR}/${slug(name)}${PRESET_SUFFIX}`;
  for (let i = 2; existing.includes(path); i++) path = `${PRESET_DIR}/${slug(name)}_${i}${PRESET_SUFFIX}`;
  await writePreset(path, { name: name.trim(), texture: { ...TEXTURE_DEFAULTS } });
  useProjectFiles.getState().refreshSoon();
}

async function addStarters(existing: readonly string[]): Promise<void> {
  for (const [file, preset] of Object.entries(STARTERS)) {
    const path = `${PRESET_DIR}/${file}${PRESET_SUFFIX}`;
    if (!existing.includes(path)) await writePreset(path, preset);
  }
  useProjectFiles.getState().refreshSoon();
}

/** One preset card: edits are saved to its file after a short pause. */
function PresetCard({ path, initial }: { path: string; initial: TexturePreset }) {
  const [preset, setPreset] = useState(initial);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  const change = (next: TexturePreset) => {
    setPreset(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void writePreset(path, next), SAVE_DELAY_MS);
  };
  const optional = (key: 'roughness' | 'metallic', v: number | undefined) => {
    const next = { ...preset };
    if (v === undefined) delete next[key];
    else next[key] = v;
    change(next);
  };
  return (
    <Card title={presetLabel(path, preset)} icon="material" defaultOpen={false} subtitle={path}>
      <PropRow label="Name">
        <TextField value={preset.name ?? ''} onChange={(v) => change({ ...preset, name: v })} />
      </PropRow>
      <PropRow label="Pattern" hint="procedural generator (see the model schema's texture patterns)">
        <SelectField
          value={String(preset.texture.pattern ?? 'noise')}
          options={['noise', ...TEXTURE_PATTERNS.filter((p) => p !== 'noise')]}
          onChange={(v) => change({ ...preset, texture: { ...preset.texture, pattern: v } })}
        />
      </PropRow>
      <ObjectFields value={preset.texture} defaults={TEXTURE_DEFAULTS} skip={['pattern']} onChange={(t) => change({ ...preset, texture: t })} />
      <PropRow label="Color" hint="optional: material color applied with the preset">
        <ColorField value={preset.color ?? [0.8, 0.8, 0.8]} onChange={(v) => change({ ...preset, color: v })} />
      </PropRow>
      <PropRow label="Roughness" hint="optional: applied with the preset">
        <NumberField value={preset.roughness} step={0.05} min={0} max={1} onChange={(v) => optional('roughness', v)} />
      </PropRow>
      <PropRow label="Metallic" hint="optional: applied with the preset">
        <NumberField value={preset.metallic} step={0.05} min={0} max={1} onChange={(v) => optional('metallic', v)} />
      </PropRow>
    </Card>
  );
}

/** The model tab's Textures panel: the project's texture presets. */
export function TexturePresetsEditor() {
  const paths = usePresetPaths();
  const presets = usePresets();
  return (
    <div className="materials">
      {paths.length === 0 && (
        <EmptyState icon="material" title="No texture presets">
          Presets are reusable procedural texture settings saved in {PRESET_DIR}/. Apply them to a material from the Materials panel.
        </EmptyState>
      )}
      {paths.map((p) => {
        const preset = presets[p];
        if (preset === undefined) return null;
        if (preset === null) {
          return (
            <div key={p} className="muted mono">
              {p}: not a valid preset (needs a "texture" object)
            </div>
          );
        }
        return <PresetCard key={p} path={p} initial={preset} />;
      })}
      <button type="button" className="ghost small add-row" onClick={() => void newPreset(paths)}>
        <Icon name="plus" size={14} /> New preset
      </button>
      <button type="button" className="ghost small add-row" onClick={() => void addStarters(paths)}>
        <Icon name="sparkles" size={14} /> Add starter presets
      </button>
    </div>
  );
}

/** Material fields to merge when applying `preset` (copies, never links). */
export function presetMaterialFields(preset: TexturePreset): Record<string, unknown> {
  const out: Record<string, unknown> = { texture: { ...TEXTURE_DEFAULTS, ...preset.texture } };
  if (Array.isArray(preset.color)) out.color = preset.color;
  if (typeof preset.roughness === 'number') out.roughness = preset.roughness;
  if (typeof preset.metallic === 'number') out.metallic = preset.metallic;
  return out;
}
